import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

// Material 3 snackbar. @material/web does not ship one yet (see its roadmap),
// so this is a faithful M3 "snackbar" built on the same --md-sys-* tokens the
// rest of the portal uses: inverse-surface container, single line of text, an
// optional action and a close affordance, bottom-centered, auto-dismissing.
//
// A single instance is mounted once (see show()/the toast() helper) and reused;
// messages queue so a burst doesn't clobber each other.

interface SnackbarMessage {
  text: string;
  isError: boolean;
  action?: { label: string; onClick: () => void };
  durationMs: number;
}

@customElement("dpp-snackbar")
export class DppSnackbar extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      z-index: 1000;
      pointer-events: none;
    }

    .snackbar {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: min(344px, 90vw);
      max-width: min(90vw, 560px);
      padding: 4px 8px 4px 16px;
      border-radius: 4px;
      background: var(--md-sys-color-inverse-surface);
      color: var(--md-sys-color-inverse-on-surface);
      box-shadow:
        0 3px 5px -1px rgba(0, 0, 0, 0.2),
        0 6px 10px 0 rgba(0, 0, 0, 0.14);
      font-size: 0.875rem;
      line-height: 1.25rem;
      opacity: 0;
      transform: translateY(8px);
      transition:
        opacity 0.2s ease,
        transform 0.2s ease;
      pointer-events: auto;
    }

    .snackbar--visible {
      opacity: 1;
      transform: translateY(0);
    }

    .snackbar--error {
      background: var(--md-sys-color-error-container);
      color: var(--md-sys-color-on-error-container);
    }

    .text {
      flex: 1;
      padding: 10px 0;
    }

    button {
      flex-shrink: 0;
      font: inherit;
      font-weight: 600;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: 4px;
      padding: 8px;
    }

    .action {
      color: var(--md-sys-color-inverse-primary, #97d4bb);
    }

    .snackbar--error .action {
      color: var(--md-sys-color-on-error-container);
    }

    .close {
      display: inline-flex;
      color: inherit;
      opacity: 0.85;
      line-height: 0;
    }

    .close:hover {
      opacity: 1;
    }

    .close svg {
      width: 18px;
      height: 18px;
      fill: currentColor;
    }
  `;

  @state() private current: SnackbarMessage | null = null;
  @state() private visible = false;

  private queue: SnackbarMessage[] = [];
  private hideTimer?: ReturnType<typeof setTimeout>;

  show(message: SnackbarMessage): void {
    this.queue.push(message);
    if (!this.current) this.next();
  }

  private next(): void {
    const msg = this.queue.shift();
    if (!msg) {
      this.current = null;
      return;
    }
    this.current = msg;
    this.visible = true;
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => this.dismiss(), msg.durationMs);
  }

  private dismiss(): void {
    this.visible = false;
    // Wait out the exit transition before showing the next message.
    setTimeout(() => this.next(), 200);
  }

  render() {
    const m = this.current;
    if (!m) return nothing;
    return html`
      <div
        class="snackbar ${this.visible ? "snackbar--visible" : ""} ${m.isError
          ? "snackbar--error"
          : ""}"
        role=${m.isError ? "alert" : "status"}
        aria-live=${m.isError ? "assertive" : "polite"}
      >
        <span class="text">${m.text}</span>
        ${m.action
          ? html`<button
              class="action"
              @click=${() => {
                m.action?.onClick();
                this.dismiss();
              }}
            >
              ${m.action.label}
            </button>`
          : nothing}
        <button
          class="close"
          aria-label="Dismiss"
          @click=${() => this.dismiss()}
        >
          <svg viewBox="0 0 24 24">
            <path
              d="M6.4 19 5 17.6l5.6-5.6L5 6.4 6.4 5l5.6 5.6L17.6 5 19 6.4 13.4 12l5.6 5.6-1.4 1.4-5.6-5.6Z"
            />
          </svg>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dpp-snackbar": DppSnackbar;
  }
}
